export type TemplateConfigJsonResult =
  | { valid: true; value: Record<string, unknown> }
  | { valid: false; message: string };

export const parseTemplateConfigJson = (
  source: string
): TemplateConfigJsonResult => {
  let value: unknown;
  try {
    value = JSON.parse(source);
  } catch {
    return {
      valid: false,
      message: "Enter valid JSON for this configuration.",
    };
  }

  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return {
      valid: false,
      message: "Template configuration must be a JSON object.",
    };
  }

  return { valid: true, value: value as Record<string, unknown> };
};

export const formatTemplateConfigJson = (
  value: Record<string, unknown>
): string => JSON.stringify(value, null, 2);

/** Key-order-independent serialization, so a round trip cannot fake a drift. */
const byKey = ([a]: [string, unknown], [b]: [string, unknown]): number => {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
};

export const stableStringify = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .sort(byKey)
      .map(([key, item]) => `${JSON.stringify(key)}:${stableStringify(item)}`);
    return `{${entries.join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
};

/**
 * Whether two template configurations are the same configuration. Compares the
 * canonicalized objects, never the formatted text: a server round trip that
 * reorders keys or re-indents is the same params and must not read as a change.
 *
 * `null` and `{}` are the same absence of configuration — the DTO uses `null`
 * for a rule that has never carried params and the buffer formats it as `{}`.
 */
export const sameTemplateParams = (
  a: Record<string, unknown> | null,
  b: Record<string, unknown> | null
): boolean => stableStringify(a ?? {}) === stableStringify(b ?? {});
